import { useEffect, useState } from "react";
import axios from "axios";
import { ShieldCheck, FileCheck, HandCoins } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMG =
  "https://images.pexels.com/photos/5063634/pexels-photo-5063634.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const DEFAULT_TITLE = "O seu próximo automóvel começa aqui.";
const DEFAULT_SUBTITLE =
  "Especialistas na comercialização e importação de viaturas da Europa para Portugal, com apoio completo ao financiamento — total transparência e segurança em cada etapa.";

export default function Hero() {
  const [hero, setHero] = useState({ hero_title: DEFAULT_TITLE, hero_subtitle: DEFAULT_SUBTITLE });

  useEffect(() => {
    axios
      .get(`${API}/settings`)
      .then((res) => {
        setHero((h) => ({
          hero_title: res.data.hero_title || h.hero_title,
          hero_subtitle: res.data.hero_subtitle || h.hero_subtitle,
        }));
      })
      .catch(() => {});
  }, []);

  return (
    <section id="inicio" data-testid="hero-section" className="relative pt-20 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-6 items-center py-16 lg:py-28">
          {/* Left copy */}
          <div className="lg:col-span-6 relative z-10">
            <p className="animate-fade-up text-xs font-semibold tracking-[0.35em] uppercase text-slate-500 mb-6">
              Comercialização · Importação · Financiamento
            </p>
            <h1
              data-testid="hero-headline"
              className="animate-fade-up delay-100 font-display text-4xl sm:text-5xl lg:text-[3.6rem] leading-[1.05] tracking-tight text-[#0072C6]"
            >
              {hero.hero_title}
            </h1>
            <p
              data-testid="hero-subtitle"
              className="animate-fade-up delay-200 mt-7 text-base sm:text-lg leading-relaxed text-slate-600 max-w-xl"
            >
              {hero.hero_subtitle}
            </p>
            <div className="animate-fade-up delay-300 mt-10 flex flex-wrap gap-4">
              <a
                href="#stock"
                data-testid="hero-cta-stock"
                className="inline-flex items-center bg-[#FF6A00] text-white font-semibold px-8 py-4 text-sm tracking-wide hover:bg-[#e05e00] transition-colors"
              >
                Ver Automóveis
              </a>
              <a
                href="/importar"
                data-testid="hero-cta-import"
                className="inline-flex items-center border border-[#0072C6] text-[#0072C6] font-semibold px-8 py-4 text-sm tracking-wide hover:bg-[#0072C6] hover:text-white transition-colors"
              >
                Importar o Meu Carro
              </a>
              <a
                href="/simulador"
                data-testid="hero-cta-financing"
                className="inline-flex items-center border border-[#0072C6] text-[#0072C6] font-semibold px-8 py-4 text-sm tracking-wide hover:bg-[#0072C6] hover:text-white transition-colors"
              >
                Simular Financiamento
              </a>
            </div>
          </div>

          {/* Right image */}
          <div className="lg:col-span-6 relative animate-fade-in delay-200">
            <div className="relative">
              <div className="absolute -top-5 -right-5 w-full h-full border border-[#0072C6]/20 hidden sm:block" />
              <img
                src={HERO_IMG}
                alt="Automóvel premium em estrada"
                data-testid="hero-image"
                className="relative w-full h-[320px] sm:h-[420px] lg:h-[500px] object-cover shadow-xl"
              />
              <div className="absolute bottom-0 left-0 bg-[#0072C6] text-white px-6 py-5 sm:px-8 sm:py-6 max-w-[280px]">
                <p className="font-display text-lg sm:text-xl leading-snug">Viaturas selecionadas da Europa</p>
                <p className="mt-1 text-xs text-slate-300 tracking-wide">Qualidade verificada, processo transparente</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Trust strip */}
      <div className="border-t border-slate-100 bg-[#F8FAFC]" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 grid sm:grid-cols-3 gap-6">
          {[
            { icon: ShieldCheck, title: "Compra Segura", text: "Processo verificado do início ao fim." },
            { icon: FileCheck, title: "Transparência Total", text: "Custos claros, sem surpresas." },
            { icon: HandCoins, title: "Financiamento Facilitado", text: "Apoio na melhor solução de crédito." },
          ].map(({ icon: Icon, title, text }, i) => (
            <div key={title} className="flex items-start gap-4" data-testid={`trust-item-${i}`}>
              <span className="shrink-0 w-11 h-11 flex items-center justify-center border border-[#0072C6]/20 text-[#0072C6]">
                <Icon className="w-5 h-5" />
              </span>
              <div>
                <p className="font-semibold text-[#0072C6] text-sm">{title}</p>
                <p className="text-sm text-slate-500 mt-0.5">{text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
